"use client";

import { cn } from "@jesus-central-church/shared";
import { Filter, Grid3X3, List } from "lucide-react";
import type { ViewMode } from "./types";
import { EVENT_TYPE_LABELS } from "./types";

export interface EventsFiltersProperties {
  selectedMonth: string;
  onMonthChange: (month: string) => void;
  selectedType: string;
  onTypeChange: (type: string) => void;
  viewMode: ViewMode;
  onViewModeChange: (mode: ViewMode) => void;
  availableMonths: { key: string; label: string }[];
  className?: string;
}

export function EventsFilters({
  selectedMonth,
  onMonthChange,
  selectedType,
  onTypeChange,
  viewMode,
  onViewModeChange,
  availableMonths,
  className,
}: EventsFiltersProperties) {
  const typeOptions = [
    { value: "all", label: "Alle types" },
    ...Object.entries(EVENT_TYPE_LABELS).map(([value, label]) => ({
      value,
      label,
    })),
  ];

  return (
    <div className={cn("border-b border-freedom/10 bg-boldness", className)}>
      <div className="container mx-auto px-6 py-6 md:px-12">
        <div className="flex flex-col gap-6 lg:flex-row lg:items-center lg:justify-between">
          {/* Month Chips */}
          <div className="flex flex-wrap gap-2">
            <button
              onClick={() => onMonthChange("all")}
              className={cn(
                "rounded-full px-4 py-2 text-sm font-medium transition-all",
                selectedMonth === "all"
                  ? "bg-freedom text-boldness"
                  : "bg-freedom/10 text-freedom/70 hover:bg-freedom/20",
              )}
            >
              Alles
            </button>
            {availableMonths.map((month) => (
              <button
                key={month.key}
                onClick={() => onMonthChange(month.key)}
                className={cn(
                  "rounded-full px-4 py-2 text-sm font-medium transition-all",
                  selectedMonth === month.key
                    ? "bg-freedom text-boldness"
                    : "bg-freedom/10 text-freedom/70 hover:bg-freedom/20",
                )}
              >
                {month.label}
              </button>
            ))}
          </div>

          {/* Right Controls */}
          <div className="flex items-center gap-4">
            {/* Type Select */}
            <div className="relative">
              <Filter className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-freedom/40" />
              <select
                value={selectedType}
                onChange={(event) => onTypeChange(event.target.value)}
                className="cursor-pointer appearance-none rounded-lg border border-freedom/20 bg-freedom/5 py-2 pr-8 pl-10 text-sm text-freedom focus:border-strategy-gold focus:outline-none"
              >
                {typeOptions.map((type) => (
                  <option
                    key={type.value}
                    value={type.value}
                    className="bg-boldness"
                  >
                    {type.label}
                  </option>
                ))}
              </select>
            </div>

            {/* View Toggle */}
            <div className="flex rounded-lg border border-freedom/20 p-1">
              <button
                onClick={() => onViewModeChange("timeline")}
                className={cn(
                  "rounded-md p-2 transition-all",
                  viewMode === "timeline"
                    ? "bg-strategy-gold text-freedom"
                    : "text-freedom/50 hover:text-freedom",
                )}
                aria-label="Tijdlijn weergave"
              >
                <Grid3X3 className="h-4 w-4" />
              </button>
              <button
                onClick={() => onViewModeChange("list")}
                className={cn(
                  "rounded-md p-2 transition-all",
                  viewMode === "list"
                    ? "bg-strategy-gold text-freedom"
                    : "text-freedom/50 hover:text-freedom",
                )}
                aria-label="Lijst weergave"
              >
                <List className="h-4 w-4" />
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
